import { Component, Inject, Input } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ICallOverview } from 'src/app/interfaces/icall-overview';
import { ICallDetailQuestion } from 'src/app/interfaces/icall-detail-question';
import { IQuestionScore } from 'src/app/interfaces/iquestion-score';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { ResponseTimeService } from 'src/app/services/general/response-time.service';

@Component({
  selector: 'app-call-guided-details-side-panel',
  templateUrl: './call-guided-details-side-panel.component.html',
  styleUrls: ['./call-guided-details-side-panel.component.scss'],
  standalone: false
})
export class CallGuidedDetailsSidePanelComponent {
  @Input() accountUuid: string = '';
  @Input() call: ICallOverview | undefined;
  @Input() moduleName: string = '';
  @Input() moduleUuid: string = '';
  totalScore: number = 5;
  activeId: string | null = null;
  iCallDetailWithQuestions: ICallDetailQuestion | undefined;
  isCallDetailsLoading: boolean = true;

  allQuestions: IQuestionScore[] = [];
  displayedQuestions: IQuestionScore[] = [];
  pageSize: number = 1;
  currentPage: number = 0;
  isNavigated: boolean = false;

  previousScrollPosition: number = 0;
  scrollDirection: 'up' | 'down' | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private reportingService: ReportingService,
    private responseTimeService: ResponseTimeService,
    private dialogRef: MatDialogRef<CallGuidedDetailsSidePanelComponent>,
  ) {}

  ngOnInit() {
    this.accountUuid = this.data.accountUuid;
    this.call = this.data.call;
    this.moduleName = this.data.moduleName;
    this.moduleUuid = this.data.moduleUuid;
    if (this.call?.callType !== 'CONVERSATIONAL') {
      this.previousScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;
      this.getCallDetails();
    } else {
      this.isCallDetailsLoading = false;
    }
  }

  calculateProgress(progress: number, total: number): any {
    if (progress < 0) progress = 0;
    if (progress > total) progress = total;
    let filled = Math.floor(progress);
    return { filled: filled, partial: progress - filled };
  }

  scrollTo(id: string) {
    this.isNavigated = true;
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        this.isNavigated = false;
        this.activeId = id;
      }, 1000);
    } else {
      let questionsToDisplay: any[] = [];
      this.displayedQuestions.forEach((dq) => {
        questionsToDisplay.push(dq.questionUuid);
      });
      questionsToDisplay.push(id);
      this.displayedQuestions = this.allQuestions.filter((item) =>
        questionsToDisplay.includes(item.questionUuid)
      );
      setTimeout(() => {
        this.loadNextQuestion();
        this.loadPreviousQuestion();
      }, 1000);
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 3500);
      setTimeout(() => {
        this.isNavigated = false;
        this.activeId = id;
      }, 5000);
    }
  }

  getCallDetails(): void {
    this.responseTimeService
      .logResponseTime(
        this.reportingService.getCallDetails(
          this.accountUuid,
          this.call?.callUuid || '',
          this.moduleUuid
        ),
        'getCallDetails'
      )
      .subscribe((response) => {
        console.log(response);
        this.iCallDetailWithQuestions = response;
        this.allQuestions = this.iCallDetailWithQuestions?.questionScores || [];
        this.isCallDetailsLoading = false;
        this.displayedQuestions.push(this.allQuestions[0]);
      });
  }

  loadNextQuestion() {
    const currentIndex = this.getCurrentIndex();

    if (currentIndex !== -1 && currentIndex < this.allQuestions.length - 1) {
      const nextQuestionId = this.allQuestions[currentIndex + 1].questionUuid;

      const isNextLoaded = this.displayedQuestions.some(
        (dq) => dq.questionUuid === nextQuestionId
      );

      if (!isNextLoaded) {
        this.updateDisplayedQuestions(nextQuestionId);
      }
    }
  }

  loadPreviousQuestion() {
    const currentIndex = this.getCurrentIndex();

    if (currentIndex > 0) {
      const previousQuestionId =
        this.allQuestions[currentIndex - 1].questionUuid;

      const isPreviousLoaded = this.displayedQuestions.some(
        (dq) => dq.questionUuid === previousQuestionId
      );

      if (!isPreviousLoaded) {
        this.updateDisplayedQuestions(previousQuestionId);
      }
    } else {
      setTimeout(() => {
        this.activeId = '';
      }, 1000);
    }
  }

  private updateDisplayedQuestions(questionId: string): void {
    const questionsToDisplay = this.displayedQuestions.map(
      (dq) => dq.questionUuid
    );
    questionsToDisplay.push(questionId);
    this.displayedQuestions = this.allQuestions.filter((item) =>
      questionsToDisplay.includes(item.questionUuid)
    );
  }

  onScroll(event: any): void {
    if (this.isNavigated) {
      event.stopPropagation();
      return;
    }
    const currentScrollPosition = this.getScrollPosition(event);
    this.detectScrollDirection(currentScrollPosition);
    let dqs = this.displayedQuestions;
    dqs.forEach((dq) => {
      const id = dq.questionUuid;
      const element = document.getElementById(id);
      if (element) {
        const visible = this.isElementInViewport(element);
        if (visible) {
          this.activeId = id;
          if (this.scrollDirection === 'down') {
            this.loadNextQuestion();
            console.log('visible and scrolling down');
          } else {
            this.loadPreviousQuestion();
            console.log('visible and scrolling up');
          }
        }
      }
    });
  }

  private getScrollPosition(event: Event): number {
    const target = event.target as HTMLElement;
    return target.scrollTop;
  }

  detectScrollDirection(currentScrollPosition: number) {
    if (currentScrollPosition > this.previousScrollPosition) {
      this.scrollDirection = 'down';
    } else if (currentScrollPosition < this.previousScrollPosition) {
      this.scrollDirection = 'up';
    }
    this.previousScrollPosition = currentScrollPosition;
  }

  isElementInViewport(el: HTMLElement): boolean {
    const rect = el.getBoundingClientRect();
    const windowHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const elementHeight = rect.bottom - rect.top;

    const isAtLeastHalfVisibleVertically =
      rect.top >= 0 && // Element's top is at or above the viewport
      rect.bottom - elementHeight / 2 <= windowHeight; // At least half of the element's bottom is within the viewport

    // Log the visibility status for debugging
    console.log(
      `Element half height ${
        isAtLeastHalfVisibleVertically
          ? 'is visible'
          : 'is not yet visible visible'
      } and user is scrolling ${this.scrollDirection}`
    );

    return isAtLeastHalfVisibleVertically;
  }

  private getCurrentIndex(): number {
    if (this.activeId) {
      return this.allQuestions.findIndex(
        (item) => item.questionUuid === this.activeId
      );
    }
    const lastDisplayQuestion =
      this.displayedQuestions[this.displayedQuestions.length - 1];
    return lastDisplayQuestion
      ? this.allQuestions.findIndex(
          (item) => item.questionUuid === lastDisplayQuestion.questionUuid
        )
      : -1;
  }

  questionNumber(id: any): number {
    const index = this.allQuestions.findIndex((qu) => qu.questionUuid === id);
    return index + 1;
  }

  close(): void {
    this.dialogRef.close();
  }
}
